import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { message, Modal } from "antd";
import { Alert } from "components/elements";

import useOptions from "features/masterData/hooks/useOptions";
import { useTranslation } from "react-i18next";
import { generateRandomString } from "utils/helper";
import LeaveTypeEntitlementForm from "../components/LeaveTypeEntitlementForm";
import LeaveTypeForm from "../components/LeaveTypeForm";
import * as services from "../services/leaveTypeApi";
import { setIsLoading, setWorkInLocation } from "../slices/attendanceSlice";
import useLeaveType from "./useLeaveType";

function toInitialValues(initialValues = {}) {

  const modifiedValues = {
    ...initialValues,
    emergencyLeave: initialValues.emergencyLeave === "Y",
    halfDayLeave: initialValues.halfDayLeave === "Y",
    consecutiveLimit: initialValues.consecutiveLimit === "Y",
    permanentOnly: initialValues.permanentOnly === "Y",
    allowOnce: initialValues.allowOnce === "Y",
    countHolidays: initialValues.countHolidays === "Y",
    extraAllowance: initialValues.extraAllowance === "Y",
    diligenceAllowance: initialValues.diligenceAllowance === "Y",
    leaveWithoutPay: initialValues.leaveWithoutPay === "Y",
    includeProbation: initialValues.includeProbation === "Y",
    avalibleInBirth: initialValues.avalibleInBirth === "Y",
    attachment: initialValues.attachment === "Y",
    accumulatedAnnualLeave: initialValues.accumulatedAnnualLeave === "Y",
    leaveGroupId: initialValues?.leaveGroup?.id ?? initialValues?.leaveGroupId,
    requestConditionId: initialValues?.requestCondition?.id,
    requestConditionCode: initialValues?.requestCondition?.code,
    gender: initialValues?.gender?.id,
    isView: false
  };

  return modifiedValues;
}

function toParams(values = {}) {
  const modifiedValues = {
    ...values,
    emergencyLeave: values.emergencyLeave ? "Y" : "N",
    halfDayLeave: values.halfDayLeave ? "Y" : "N",
    consecutiveLimit: values.consecutiveLimit ? "Y" : "N",
    permanentOnly: values.permanentOnly ? "Y" : "N",
    allowOnce: values.allowOnce ? "Y" : "N",
    countHolidays: values.countHolidays ? "Y" : "N",
    extraAllowance: values.extraAllowance ? "Y" : "N",
    diligenceAllowance: values.diligenceAllowance ? "Y" : "N",
    leaveWithoutPay: values.leaveWithoutPay ? "Y" : "N",
    includeProbation: values.includeProbation ? "Y" : "N",
    avalibleInBirth: values.avalibleInBirth ? "Y" : "N",
    attachment: values.attachment ? "Y" : "N",
    accumulatedAnnualLeave: values.accumulatedAnnualLeave ? "Y" : "N",
    leaveCalcultation: values?.leaveCalcultation?.id
  };
  return modifiedValues;
}

function toParamsLeaveEntitlement(values = {}) {
  const modifiedValues = {
    leaveTypeId: values?.leaveTypeCompanyId,
    companyId: values?.companyId,
    leaveConditionPosition: {
      isAllPosition: values?.isAllPosition ? values?.isAllPosition : 'N',
      conditionByPosition: values?.conditionByPosition?.map((item) => {
        return {
          ...item,
          determineByRank: item?.determineByRank ? item?.determineByRank : 'N',
          leavePermissionTime: {
            ...item?.leavePermissionTime,
            statusFlag: 'ACTIVE',
            determineByWorkExp: item?.leavePermissionTime?.determineByWorkExp ? item?.leavePermissionTime?.determineByWorkExp : 'N',
            durationTime: item?.leavePermissionTime?.durationTime.map((e) => {
              return {
                ...e,
                prorate: e?.prorate ? e?.prorate : 'N',
                unlimited: e?.unlimited ? e?.unlimited : 'N',
                statusFlag: 'ACTIVE'
              }
            }) || []
          }
        };
      }) || [],
      conditionForAllPosition: {
        determineByRank: values?.conditionForAllPosition?.determineByRank ? values?.conditionForAllPosition?.determineByRank : 'N',
        leavePermissionTime: {
          ...values?.conditionForAllPosition?.leavePermissionTime,
          statusFlag: 'ACTIVE',
          durationTime: values?.conditionForAllPosition?.leavePermissionTime?.durationTime?.map((e) => {
            return {
              ...e,
              statusFlag: 'ACTIVE',
            }
          }) || []
        }
      }

    },
  };
  return modifiedValues;
}

function toInitialValuesEntitlement(initialValues = {}) {
  const modifiedValues = {
    isAllPosition: initialValues?.leaveConditionPosition?.isAllPosition ? initialValues?.leaveConditionPosition?.isAllPosition : 'Y',
    conditionByPosition: initialValues?.leaveConditionPosition?.conditionByPosition,
    conditionForAllPosition: initialValues?.leaveConditionPosition?.conditionForAllPosition,
    isView: false
  };
  return modifiedValues;
}



function useLeaveTypeEdit() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const params = useParams();

  const { t, i18n } = useTranslation();

  const currentCompany = useSelector((state) => state.company.currentCompany);
  const currentEmployee = useSelector((state) => state.company.currentEmployee);
  const isLoading = useSelector((state) => state.attendance.isLoading);
  const [selectTab, setSelectTab] = useState("LeaveTypeForm");
  const leaveTypeById = useSelector((state) => state.attendance.leaveTypeById);
  const { workInLocation } = useSelector((state) => state.attendance);
  const { getLeaveTypeById } = useLeaveType();

  const {
    leaveGroupOptions,
    leaveRequestConditionOptions,
    minimumLeaveGroupOptions,
    documentLimitGroupOptions,
    monthWeekDayWorkingOptions,
    genderOptions,
    leaveCalculationOptions,
    // positionOptions,
    // getPosition,
    getLeaveGroupOptions,
    getLeaveRequestConditionOptions,
    getMinimumLeaveGroupOptions,
    getDocumentLimitGroupOptions,
    getMonthWeekDayWorkingOptions,
    getGenderOptions,
    getLeaveCalculationOptions,
  } = useOptions();

  const [positionOptions, setPositionOptions] = useState([]);

  const modifiedPositionOptions = useMemo(() => {
    const modifiedOptions = positionOptions.map((opt) => ({
      ...opt,
      label: i18n.language === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [i18n, positionOptions]);

  const modifiedWorkInLocationOption = useMemo(() => {
    return workInLocation?.map((o) => ({
      value: o?.workInLocationId,
      label: o?.workInLocationName
    }))
  }, [workInLocation])

  const useFetchOptions = (serviceFunction, companyId, groupType, dispatchFunction) => {
    const dispatch = useDispatch();
    useEffect(() => {
      const fetchData = async () => {
        dispatch(setIsLoading(true));
        try {
          const response = await serviceFunction({
            requestId: generateRandomString(),
            companyId,
            groupType,
          });
          dispatch(dispatchFunction(response.data));
        } catch (error) {
          console.error('Failed to fetch data:', error);
        } finally {
          dispatch(setIsLoading(false));
        }
      };

      if (companyId) {
        fetchData();
      }
    }, [companyId, serviceFunction, groupType, dispatchFunction, dispatch]);
  };

  useFetchOptions(services.getPositionOptions, currentCompany, "", setPositionOptions);

  const getLeaveEntitlement = useCallback(async () => {
    if (typeof currentCompany !== "object") {
      try {
        dispatch(setIsLoading(true));
        const values = {
          requestId: generateRandomString(),
          companyId: currentCompany,
          leaveTypeCompanyId: params?.id
        };
        const response = await services.getLeaveEntitlement(values);
        setInitialEntitlement(toInitialValuesEntitlement({ ...response?.data, isView: location?.state?.isView })

        )
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    }
  }, [currentCompany, dispatch])

  const [initialEntitlement, setInitialEntitlement] = useState({})

  useEffect(() => {
    setInitialEntitlement({
      isAllPosition: true,
      // leavePermissionTime: []
    })
  }, []);

  const initialValues = useMemo(() => {
    let leaveGroup = leaveGroupOptions?.find(o => o.id == leaveTypeById?.leaveGroup?.id);
    let expiringMonth = monthWeekDayWorkingOptions?.find(o => o.id == leaveTypeById?.expiringMonth?.id)?.id;
    let leaveCalculation = leaveCalculationOptions?.find(o => o.id == leaveTypeById?.leaveCalculation?.id)?.id;

    // console.log('location.state', location.state);

    return toInitialValues({
      // ...location.state,
      ...leaveTypeById,
      stpLeaveTypeId: params.id,
      employeeId: currentEmployee,
      leaveGroupName: leaveGroup?.nameEn,
      expiringMonth,
      leaveCalculation,
      leaveGroupCode: leaveTypeById?.leaveGroup?.code,
      requestConditionCode: leaveTypeById?.requestCondition?.code
    });

  }, [currentEmployee, leaveCalculationOptions, leaveGroupOptions, leaveTypeById, monthWeekDayWorkingOptions, params.id]);

  const onCancel = useCallback(() => {
    navigate("/configuration/attendance", { state: { selectedTab: 'leave' } });
  }, [navigate]);


  const updateLeaveTypeCompany = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        const result = await services.updateLeaveTypeCompany(
          toParams({
            ...values,
            companyId: currentCompany,
            employeeId: currentEmployee
          })
        );
        // toParams({ ...values, companyId: currentCompany })
        message.success(t("common.successsfull"));
        getLeaveTypeById(values?.id);
        navigate("/configuration/attendance", { state: { selectedTab: 'leave' } })

      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, currentEmployee, dispatch, getLeaveTypeById]
  );

  const getDropdownWorkInLocationForOffsite = useCallback(async () => {
    try {
      dispatch(setIsLoading(true));
      const response = await services.dropdownWorkInLocation({
        companyId: currentCompany,
        employeeId: currentEmployee,
        actionId: currentEmployee
      })
      dispatch(setWorkInLocation(response?.data?.content))
      console.log("data fetch ", response?.data?.content);

    } catch (error) {
      console.log(error);
    } finally {
      dispatch(setIsLoading(false));
    }


  }, [currentCompany, currentEmployee])

  const updateLeaveTypeEntitlement = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        const result = await services.updateLeaveTypeEntitlement(
          toParamsLeaveEntitlement({ ...values, companyId: currentCompany, leaveTypeCompanyId: params?.id })
        );
        // toParams({ ...values, companyId: currentCompany })
        // Alert({ resultObject: result });

        message.success(t("common.successsfull"));
        getLeaveEntitlement();

      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch, onCancel]
  );

  const handleMenuChange = useCallback((e) => {
    setSelectTab(e)
  }, []);

  const formChange = useCallback((props) => {
    if (selectTab === "LeaveTypeForm") {
      return <LeaveTypeForm {...props} type={params.type} />
    } else if (selectTab !== "LeaveTypeForm" && props?.initialValues?.leaveGroup?.code === 'LEAVE_GROUP_01') {
      return <LeaveTypeEntitlementForm {...props} type={params.type} />
    }
  }, [selectTab])


  const onSubmit = useCallback(
    (values) => {
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (
          <>
            <p>{t("generaInfo.are-you-sure-confirm-to-edit")}</p>
          </>
        ),
        onOk() {
          updateLeaveTypeCompany(values);
        },
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
      });
    },
    [updateLeaveTypeCompany]
  );

  const onSubmitLeaveEntitlement = useCallback(
    (values) => {
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (
          <>
            <p>{t("generaInfo.are-you-sure-confirm-to-edit")}</p>
          </>
        ),
        onOk() {
          updateLeaveTypeEntitlement(values);
        },
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
      });
    },
    [updateLeaveTypeEntitlement]
  );

  useEffect(() => {
    getLeaveGroupOptions();
    // getPosition();
    getLeaveEntitlement()
  }, [getLeaveGroupOptions, getLeaveEntitlement]);

  useEffect(() => {
    getMinimumLeaveGroupOptions();
  }, [getMinimumLeaveGroupOptions]);

  useEffect(() => {
    getDocumentLimitGroupOptions();
  }, [getDocumentLimitGroupOptions]);

  useEffect(() => {
    getLeaveRequestConditionOptions();
  }, [getLeaveRequestConditionOptions]);

  useEffect(() => {
    getMonthWeekDayWorkingOptions();
  }, [getMonthWeekDayWorkingOptions]);

  useEffect(() => {
    getGenderOptions();
  }, [getGenderOptions]);

  useEffect(() => {


    if (currentCompany) {
      getDropdownWorkInLocationForOffsite()
    }
  }, [currentCompany])


  useEffect(() => {
    getLeaveCalculationOptions();
  }, [getLeaveCalculationOptions]);

  return {
    isLoading,
    initialValues,
    initialEntitlement,
    leaveGroupOptions,
    leaveRequestConditionOptions,
    minimumLeaveGroupOptions,
    documentLimitGroupOptions,
    monthWeekDayWorkingOptions,
    genderOptions,
    leaveCalculationOptions,
    onCancel,
    onSubmit,
    onSubmitLeaveEntitlement,
    positionOptions: modifiedPositionOptions,
    formChange,
    handleMenuChange,
    modifiedWorkInLocationOption,
    getDropdownWorkInLocationForOffsite
  };
}

export default useLeaveTypeEdit;
